/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Frontend component displaying global leaderboards with multiple filters.
 */
import React, { useEffect, useState } from "react";
import { Trophy, Zap, Crosshair, Users, DollarSign } from "lucide-react";
import { callBackend } from "../utils/api";

export default function Leaderboard({ onInspect }) {
  const [topPlayers, setTopPlayers] = useState([]);
  const [sortType, setSortType] = useState("level");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setTopPlayers([]); // [FIX] Clear old data immediately to prevent render mismatch
    callBackend(`/leaderboard/fetch?sort=${sortType}`)
      .then((data) => {
        setTopPlayers(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sortType]);

  const renderHeader = () => {
    if (sortType === "alliance") {
      return (
        <tr className="text-zinc-500 text-xs border-b border-zinc-900 uppercase">
          <th className="pb-2">Rank</th>
          <th className="pb-2">Alliance</th>
          <th className="pb-2 text-right">Members</th>
          <th className="pb-2 text-right">Total Power</th>
        </tr>
      );
    }
    return (
      <tr className="text-zinc-500 text-xs border-b border-zinc-900 uppercase">
        <th className="pb-2">Rank</th>
        <th className="pb-2">User</th>
        <th className="pb-2 text-right">
          {sortType === "bounty"
            ? "Bounty Earnings"
            : sortType === "wealth"
              ? "Cash (Liquid)"
              : sortType === "power"
                ? "Street Power"
                : "Level"}
        </th>
      </tr>
    );
  };

  const renderRow = (item, index) => {
    // --- RENDER ALLIANCE ROW ---
    if (sortType === "alliance") {
      return (
        <tr
          key={item.id}
          className="border-b border-zinc-900 group hover:bg-zinc-900/50 transition-colors"
        >
          <td className="py-4 text-zinc-500 font-bold">#{index + 1}</td>
          <td className="py-4">
            <div className="flex flex-col">
              <span className="text-white font-black uppercase text-sm">
                [{item.tag}] {item.name}
              </span>
              <span className="text-[10px] text-zinc-500 uppercase">
                Leader: {item.leader_name}
              </span>
            </div>
          </td>
          <td className="py-4 text-right text-zinc-400 font-mono text-xs">
            {item.member_count} Members
          </td>
          <td className="py-4 text-right">
            <span className="text-yellow-500 font-black flex items-center justify-end gap-1">
              <Zap size={12} />{" "}
              {(parseInt(item.total_power) || 0).toLocaleString()}
            </span>
          </td>
        </tr>
      );
    }

    // --- RENDER PLAYER ROW ---
    return (
      <tr
        key={item.id}
        onClick={() => onInspect(item)}
        className="border-b border-zinc-900 group hover:bg-zinc-950 cursor-pointer transition-colors"
      >
        <td className="py-4 text-zinc-500 font-bold">#{index + 1}</td>
        <td className="py-4">
          <div className="flex items-center gap-2">
            {item.is_active ? (
              <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </div>
            ) : (
              <div className="h-2 w-2 rounded-full bg-zinc-800"></div>
            )}

            <div className="flex flex-col">
              <div className="flex items-center gap-2 font-black">
                {(item.total_bounty || 0) > 0 && (
                  <Crosshair size={14} className="text-red-600 animate-pulse" />
                )}
                {item.tag && (
                  <span className="text-blue-500 italic">[{item.tag}]</span>
                )}
                <span
                  style={{
                    color: item.prestige_level > 0 ? item.level_color : "",
                  }}
                >
                  {item.username}
                </span>
              </div>
              {item.is_active && (
                <span className="text-[9px] text-green-500 uppercase font-black tracking-tighter leading-none">
                  Active on the block
                </span>
              )}
            </div>
          </div>
        </td>

        <td className="py-4 text-right font-bold">
          {sortType === "bounty" && (
            <span className="text-red-500 flex items-center justify-end gap-1">
              <Crosshair size={12} /> $
              {(item.bounty_earnings || 0).toLocaleString()}
            </span>
          )}
          {sortType === "wealth" && (
            <span className="text-green-500 flex items-center justify-end gap-1">
              <DollarSign size={12} /> ${(item.cash || 0).toLocaleString()}
            </span>
          )}
          {sortType === "power" && (
            <span className="text-yellow-500 flex items-center justify-end gap-1">
              <Zap size={12} />{" "}
              {Math.round(item.street_power || 0).toLocaleString()}
            </span>
          )}
          {sortType === "level" && (
            <span
              style={{ color: item.level_color }}
              className="uppercase italic font-black flex items-center justify-end gap-1"
            >
              Lvl {item.level}
            </span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-black border-2 border-zinc-900 hover:border-yellow-900/40 p-6 shadow-2xl font-mono transition-all duration-300 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-end mb-6 border-b border-zinc-800 pb-4 gap-4">
        <div className="flex items-center gap-3">
          <Trophy className="text-yellow-500" size={32} />
          <div>
            <h2 className="text-3xl font-black italic uppercase text-white leading-none">
              The Top 100
            </h2>
            <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
              {sortType === "alliance"
                ? "Dominant Crews"
                : "Notorious Kingpins"}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { id: "level", label: "Level", icon: null },
            { id: "power", label: "Power", icon: Zap },
            { id: "wealth", label: "Wealth", icon: DollarSign },
            { id: "bounty", label: "Bounty", icon: Crosshair },
            { id: "alliance", label: "Alliance", icon: Users },
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => setSortType(type.id)}
              className={`px-3 py-2 text-[10px] font-black uppercase border transition-all flex items-center gap-2 ${
                sortType === type.id
                  ? "bg-zinc-100 text-black border-white"
                  : "text-zinc-500 border-zinc-800 hover:text-white hover:border-zinc-600 bg-zinc-950"
              }`}
            >
              {type.icon && <type.icon size={12} />} {type.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 font-mono uppercase py-20 animate-pulse">
          Gathering Intelligence...
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>{renderHeader()}</thead>
            <tbody className="text-sm">
              {topPlayers.map((player, index) => renderRow(player, index))}
            </tbody>
          </table>
          {topPlayers.length === 0 && (
            <div className="p-8 text-center text-zinc-600 uppercase text-xs italic border-b border-zinc-900">
              No records found on the streets.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
